import React, { useEffect, useRef, useState } from "react";
import { Button } from "../../../components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../../components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import {
	FileText,
	FileSpreadsheet,
	FileDown,
	FileArchive,
	Download,
} from "lucide-react";
import { Checkbox } from "../../../components/ui/checkbox";
import { Progress } from "../../../components/ui/progress";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { TooltipWrapper } from "../../../components/ui/tooltip-wrapper";
import { Logger } from "@/lib/logger/Logger";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { ExportHeaderMetadata } from "../../../lib/utils/export-utils";
import type {
	ExportData,
	ExportDataResult,
	ExportFormat,
	TableExportOptions,
} from "../../../hooks/useTableExport";
import type { TableInvoiceRow } from "../types/invoice-table.types";
import {
	countInvoicePdfs,
	estimateZipSizeMb,
	InvoicePdfProgress,
	InvoicePdfZipSummary,
	MAX_INVOICE_PDFS_PER_ZIP,
	MISSING_INVOICE_PDFS_FILENAME,
} from "../../../lib/utils/invoice-pdf-zip";

const columnNameTranslations: Record<string, string> = {
	// Invoice columns
	invoice_doc_number: "Rechnungsnummer",
	invoice_date: "Rechnungsdatum",
	invoice_value: "Betrag",
	invoice_currency: "Währung",
	invoice_canceled_at: "Storniert am",
	reason_for_cancelation: "Stornogrund",
	// Recipient/Client columns
	recipient_name: "Empfänger",
	recipient_company_name: "Empfänger Firma",
	recipient_type: "Empfängertyp",
	client_name: "Kunde",
	client_last_name: "Kunde",
	client_company_name: "Kunde Firma",
	// Status columns
	status: "Status",
	payment_status: "Zahlungsstatus",
	zak_status: "ZAK Status",
	is_sent_to_zak: "ZAK gesendet",
	shared_status: "Geteilt",
	shared_at: "Geteilt am",
	// Tax/Amount columns
	tax_rate: "MwSt.-Satz",
	effective_tax_rate: "MwSt.-Satz",
	net_amount: "Netto",
	vat_amount: "MwSt.-Betrag",
	gross_amount: "Brutto",
	// Payment columns
	payment_percentage: "Zahlungsfortschritt",
	total_allocated_amount: "Bezahlter Betrag",
	valuta_date: "Valutadatum",
	open_amount: "Offener Betrag",
	"payment-status": "Zahlungsstatus",
	"payment-type": "Zahlungsart",
	"payment-amount": "Zahlungsbetrag",
	"payment-date": "Zahlungsdatum",
	"payment-note": "Notiz",
	"payment-cancellation-reason": "Stornogrund",
	// Filter columns
	invoice_value_min: "Mindestbetrag",
	invoice_value_max: "Höchstbetrag",
};

const translateColumnName = (columnKey: string): string => {
	return columnNameTranslations[columnKey] || columnKey;
};

const translateSortingString = (sortingStr: string): string => {
	return sortingStr.replace(
		/^([a-z_]+)(\s*\()/i,
		(_, key, paren) => translateColumnName(key) + paren
	);
};

const describeFilters = ({
	dateRange,
	recipientFilter,
	globalFilter,
}: ExportHeaderMetadata): string =>
	[
		dateRange && `Zeitraum: ${dateRange.start} – ${dateRange.end}`,
		recipientFilter && `Empfänger: ${recipientFilter}`,
		globalFilter && `Suche: "${globalFilter}"`,
	]
		.filter(Boolean)
		.join(", ") || "Keine Filter aktiv";

const getExportNotification = (
	format: ExportFormat,
	invoicePdfs: InvoicePdfZipSummary | null
) => {
	if (!invoicePdfs) {
		return {
			message: `${format.toUpperCase()} Export erfolgreich heruntergeladen`,
			type: "success" as const,
		};
	}
	if (invoicePdfs.missing === 0) {
		return {
			message: `ZIP mit ${invoicePdfs.added.toLocaleString("de-CH")} ${
				invoicePdfs.added === 1 ? "Rechnungs-PDF" : "Rechnungs-PDFs"
			} heruntergeladen`,
			type: "success" as const,
		};
	}
	return {
		message: `ZIP heruntergeladen – ${invoicePdfs.missing.toLocaleString(
			"de-CH"
		)} ${invoicePdfs.missing === 1 ? "Rechnung" : "Rechnungen"} ohne PDF`,
		description: `Die betroffenen Rechnungen stehen in ${MISSING_INVOICE_PDFS_FILENAME} im ZIP.`,
		type: "warning" as const,
	};
};

export interface ExportDialogProps {
	invoiceData: ExportData;
	loading?: boolean;
	onExport: (
		exportData: ExportData,
		format: ExportFormat,
		options: TableExportOptions,
		headerMetadata: ExportHeaderMetadata
	) => Promise<InvoicePdfZipSummary | null>;
	fetchExportData?: () => Promise<ExportDataResult>;
	currentSorting?: string;
	currentFilters?: string;
	headerMetadata: ExportHeaderMetadata;
	showPaymentsOption?: boolean;
	showInvoicesOption?: boolean;
	showInvoicePdfsOption?: boolean;
	incompleteDateRange?: boolean;
}

export const TableExportDialog: React.FC<ExportDialogProps> = ({
	invoiceData,
	loading = false,
	onExport,
	fetchExportData,
	headerMetadata,
	currentSorting = "Rechnungsdatum",
	currentFilters = describeFilters(headerMetadata),
	showPaymentsOption = false,
	showInvoicesOption = false,
	showInvoicePdfsOption = false,
	incompleteDateRange = false,
}) => {
	const [open, setOpen] = useState(false);
	const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("csv");
	const [exporting, setExporting] = useState(false);
	const [exportData, setExportData] = useState<ExportData>([]);
	const [availableRowCount, setAvailableRowCount] = useState<number | null>(
		null
	);
	const [fetchingData, setFetchingData] = useState(false);
	const [includePayments, setIncludePayments] = useState(false);
	const [includeInvoices, setIncludeInvoices] = useState(false);
	const [includeInvoicePdfs, setIncludeInvoicePdfs] = useState(false);
	const [pdfProgress, setPdfProgress] = useState<InvoicePdfProgress | null>(
		null
	);
	const [confirmingCancel, setConfirmingCancel] = useState(false);
	const fetchRequestRef = useRef(0);
	const exportAbortRef = useRef<AbortController | null>(null);

	const stopGuardingUnloadRef = useRef<(() => void) | null>(null);

	useEffect(
		() => () => {
			exportAbortRef.current?.abort();
			stopGuardingUnloadRef.current?.();
		},
		[]
	);

	const currentData = fetchExportData ? exportData : invoiceData;
	const isDisabled =
		loading ||
		invoiceData.length === 0 ||
		fetchingData ||
		incompleteDateRange;
	const hasData = invoiceData.length > 0;
	const truncatedRowCount =
		availableRowCount !== null && availableRowCount > currentData.length
			? availableRowCount
			: null;
	const pdfCount = showInvoicePdfsOption
		? countInvoicePdfs(currentData as TableInvoiceRow[])
		: 0;
	const tooManyInvoicePdfs =
		includeInvoicePdfs && pdfCount > MAX_INVOICE_PDFS_PER_ZIP;
	const exportBlocked =
		currentData.length === 0 ||
		truncatedRowCount !== null ||
		tooManyInvoicePdfs;

	const resetExportData = () => {
		fetchRequestRef.current++;
		setExportData([]);
		setAvailableRowCount(null);
		setFetchingData(false);
		setPdfProgress(null);
	};

	const handleOpenChange = async (newOpen: boolean) => {
		if (exporting) {
			setConfirmingCancel(true);
			return;
		}
		setOpen(newOpen);
		resetExportData();
		if (!newOpen || !fetchExportData) return;

		const requestId = fetchRequestRef.current;
		setFetchingData(true);
		try {
			const result = await fetchExportData();
			if (requestId !== fetchRequestRef.current) return;
			if (result.error) throw new Error(result.error);
			setExportData(result.data);
			setAvailableRowCount(result.totalCount);
		} catch (error) {
			if (requestId !== fetchRequestRef.current) return;
			Logger.error(error, {}, "Failed to fetch export data");
			showNotification({
				message:
					"Fehler beim Laden der Daten für den Export, bitte öffnen Sie den Dialog erneut",
				type: "error",
			});
		} finally {
			if (requestId === fetchRequestRef.current) setFetchingData(false);
		}
	};

	const handleExport = async () => {
		if (exporting || loading || fetchingData || exportBlocked) return;

		const controller = new AbortController();
		exportAbortRef.current = controller;
		const handleBeforeUnload = (event: BeforeUnloadEvent) =>
			event.preventDefault();
		window.addEventListener("beforeunload", handleBeforeUnload);
		stopGuardingUnloadRef.current = () =>
			window.removeEventListener("beforeunload", handleBeforeUnload);
		setExporting(true);
		try {
			const invoicePdfs = await onExport(
				currentData,
				selectedFormat,
				{
					includePayments,
					includeInvoices,
					includeInvoicePdfs,
					signal: controller.signal,
					onProgress: setPdfProgress,
				},
				headerMetadata
			);
			showNotification(
				getExportNotification(selectedFormat, invoicePdfs)
			);
			setOpen(false);
			resetExportData();
		} catch (error) {
			if (controller.signal.aborted) {
				setOpen(false);
				resetExportData();
				return;
			}
			Logger.error(error, {}, "Failed to export data");
			showNotification({
				message:
					error instanceof Error
						? error.message
						: "Export fehlgeschlagen",
				type: "error",
			});
		} finally {
			stopGuardingUnloadRef.current?.();
			stopGuardingUnloadRef.current = null;
			exportAbortRef.current = null;
			setExporting(false);
			setConfirmingCancel(false);
			setPdfProgress(null);
		}
	};

	const handleConfirmCancel = () => {
		exportAbortRef.current?.abort();
		setConfirmingCancel(false);
	};

	const formatIcons = {
		csv: FileText,
		excel: FileSpreadsheet,
		pdf: FileDown,
	};

	const formatLabels = {
		csv: "CSV (Komma-getrennte Werte)",
		excel: "Excel (Microsoft Excel Format)",
		pdf: "PDF (Druckfähiges Dokument)",
	};

	const FormatIcon = includeInvoicePdfs
		? FileArchive
		: formatIcons[selectedFormat];
	const submitLabel = fetchingData
		? "Lade Daten..."
		: exporting
			? "Exportiere..."
			: `Als ${
					includeInvoicePdfs ? "ZIP" : selectedFormat.toUpperCase()
				} exportieren`;
	const pdfHint = tooManyInvoicePdfs
		? `${pdfCount.toLocaleString(
				"de-CH"
			)} PDFs – höchstens ${MAX_INVOICE_PDFS_PER_ZIP.toLocaleString(
				"de-CH"
			)} pro ZIP. Bitte Zeitraum eingrenzen.`
		: `${pdfCount.toLocaleString("de-CH")} ${
				pdfCount === 1 ? "Rechnungs-PDF" : "Rechnungs-PDFs"
			} (~${estimateZipSizeMb(pdfCount).toLocaleString("de-CH")} MB)`;
	const openButtonTooltip = incompleteDateRange
		? "Bitte Start- und Enddatum wählen"
		: hasData
			? "Tabelle exportieren"
			: "Keine Daten zum Exportieren vorhanden";
	const rowCountLabel = fetchingData
		? "Lade..."
		: currentData.length.toLocaleString("de-CH");

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			<TooltipWrapper title={openButtonTooltip}>
				<span className="inline-flex">
					<DialogTrigger asChild>
						<Button
							variant="outline"
							disabled={isDisabled}
							className="flex items-center gap-2"
							aria-label="Exportieren"
							data-testid="table-export-open-button"
						>
							<Download className="h-4 w-4" />
							Exportieren
						</Button>
					</DialogTrigger>
				</span>
			</TooltipWrapper>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Tabelle exportieren</DialogTitle>
					<DialogDescription>
						Wählen Sie das Exportformat. Die aktuellen Filter und
						Sortierung werden übernommen.
					</DialogDescription>
				</DialogHeader>
				<div className="grid gap-4 py-4">
					<div className="grid gap-2">
						<Label htmlFor="format">Format</Label>
						<Select
							value={selectedFormat}
							onValueChange={(value: ExportFormat) =>
								setSelectedFormat(value)
							}
							disabled={exporting}
						>
							<SelectTrigger id="format">
								<SelectValue placeholder="Format wählen" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="csv">
									<div className="flex items-center gap-2">
										<FileText className="h-4 w-4" />
										<span>CSV</span>
									</div>
								</SelectItem>
								<SelectItem value="excel">
									<div className="flex items-center gap-2">
										<FileSpreadsheet className="h-4 w-4" />
										<span>Excel</span>
									</div>
								</SelectItem>
								<SelectItem value="pdf">
									<div className="flex items-center gap-2">
										<FileDown className="h-4 w-4" />
										<span>PDF</span>
									</div>
								</SelectItem>
							</SelectContent>
						</Select>
						<p className="text-xs text-gray-500">
							{formatLabels[selectedFormat]}
						</p>
					</div>
					{showPaymentsOption && (
						<div className="flex items-center space-x-2">
							<Checkbox
								id="include-payments"
								checked={includePayments}
								onCheckedChange={(checked) =>
									setIncludePayments(checked === true)
								}
								disabled={exporting}
							/>
							<Label
								htmlFor="include-payments"
								className="cursor-pointer"
							>
								Zahlungen einschliessen
							</Label>
						</div>
					)}
					{showInvoicesOption && (
						<div className="flex items-center space-x-2">
							<Checkbox
								id="include-invoices"
								checked={includeInvoices}
								onCheckedChange={(checked) =>
									setIncludeInvoices(checked === true)
								}
								disabled={exporting}
							/>
							<Label
								htmlFor="include-invoices"
								className="cursor-pointer"
							>
								Rechnungen einschliessen
							</Label>
						</div>
					)}
					{showInvoicePdfsOption && (
						<div className="grid gap-1">
							<div className="flex items-center space-x-2">
								<Checkbox
									id="include-invoice-pdfs"
									checked={includeInvoicePdfs}
									onCheckedChange={(checked) =>
										setIncludeInvoicePdfs(checked === true)
									}
									disabled={exporting}
									aria-label="Rechnungs-PDFs beilegen (ZIP)"
									data-testid="table-export-include-invoice-pdfs"
								/>
								<Label
									htmlFor="include-invoice-pdfs"
									className="cursor-pointer"
								>
									Rechnungs-PDFs beilegen (ZIP)
								</Label>
							</div>
							{includeInvoicePdfs && !fetchingData && (
								<p
									className={
										tooManyInvoicePdfs
											? "text-xs text-red-600"
											: "text-xs text-gray-500"
									}
									data-testid="table-export-invoice-pdf-hint"
								>
									{pdfHint}
								</p>
							)}
						</div>
					)}
					<div className="grid gap-2">
						<Label>Aktuelle Tabellen-Einstellungen</Label>
						<div className="text-sm text-gray-600 space-y-1">
							<div>
								Sortierung:{" "}
								{translateSortingString(currentSorting)}
							</div>
							<div>Filter: {currentFilters}</div>
							<div>Anzahl Einträge: {rowCountLabel}</div>
						</div>
						{truncatedRowCount !== null ? (
							<p
								className="text-xs text-red-600"
								role="alert"
								data-testid="table-export-truncated-hint"
							>
								{`Es wurden ${truncatedRowCount.toLocaleString(
									"de-CH"
								)} Einträge gefunden, exportiert werden können höchstens ${currentData.length.toLocaleString(
									"de-CH"
								)}. Bitte Zeitraum oder Filter eingrenzen.`}
							</p>
						) : (
							<p className="text-xs text-gray-500">
								Der Export verwendet die aktuellen Filter und
								Sortierung der Tabelle.
							</p>
						)}
					</div>
					{pdfProgress && (
						<div
							className="grid gap-2"
							aria-live="polite"
							data-testid="table-export-pdf-progress"
						>
							<Progress
								value={
									pdfProgress.total === 0
										? 100
										: (pdfProgress.loaded /
												pdfProgress.total) *
											100
								}
								className="h-2"
							/>
							<p className="text-xs text-gray-500">
								{pdfProgress.loaded < pdfProgress.total
									? `Rechnungs-PDFs werden geladen … ${pdfProgress.loaded.toLocaleString(
											"de-CH"
										)} / ${pdfProgress.total.toLocaleString(
											"de-CH"
										)}`
									: "ZIP wird erstellt …"}
							</p>
						</div>
					)}
				</div>
				<DialogFooter>
					<Button
						variant="outline"
						onClick={() => handleOpenChange(false)}
						aria-label="Abbrechen"
						data-testid="table-export-cancel-button"
					>
						Abbrechen
					</Button>
					<Button
						onClick={handleExport}
						disabled={exporting || fetchingData || exportBlocked}
						className="flex items-center gap-2"
						aria-label={submitLabel}
						data-testid="table-export-submit-button"
					>
						{exporting || fetchingData ? (
							<div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
						) : (
							<FormatIcon className="h-4 w-4" />
						)}
						{submitLabel}
					</Button>
				</DialogFooter>
			</DialogContent>
			<AlertDialog
				open={confirmingCancel}
				onOpenChange={setConfirmingCancel}
			>
				<AlertDialogContent data-testid="table-export-cancel-confirm">
					<AlertDialogHeader>
						<AlertDialogTitle>Export abbrechen?</AlertDialogTitle>
						<AlertDialogDescription>
							Der Export läuft noch. Wenn Sie abbrechen, wird
							keine Datei gespeichert.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel
							aria-label="Weiter exportieren"
							data-testid="table-export-continue-button"
						>
							Weiter exportieren
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleConfirmCancel}
							aria-label="Export abbrechen"
							data-testid="table-export-confirm-cancel-button"
						>
							Export abbrechen
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</Dialog>
	);
};
